/**
 * Thin client for the Kids Puzzle Worker API.
 *
 * WORKER_BASE is the only value that needs updating if the Worker URL changes.
 * For local dev the Worker is still hit over the network; if CORS blocks it
 * (e.g. running against a dev server on a different port) callers should fall
 * back to the static word banks.
 */

import type { AgeGroup, Language } from '../types'
import type { WordEntry } from './crossword/wordbank'
import type { StaticRebusPuzzle } from './rebus/puzzles'

// ── Change this one line if the Worker URL ever changes ──
const WORKER_BASE = 'https://kids-puzzle-worker.p-znuderl.workers.dev'

// ─── Word bank ────────────────────────────────────────────────────────────────

interface WordsResponse {
  words: Array<{
    id:         string
    word:       string
    language:   string
    age_group:  string
    avg_rating: number
    clues:      string[]
  }>
  count: number
}

// ─── Rebus puzzles ────────────────────────────────────────────────────────────

interface RebusResponse {
  rebuses: Array<{
    id:          string
    target_word: string
    tiles:       StaticRebusPuzzle['tiles']
    hint:        string
    language:    string
    age_group:   string
    avg_rating:  number
  }>
  count: number
}

/**
 * Fetch approved rebus puzzles for a language + age-group from D1.
 * Returns null if the request fails (caller should fall back to static bank).
 */
export async function fetchRebusPuzzles(
  language: Language,
  ageGroup: AgeGroup,
  count:    number,
): Promise<StaticRebusPuzzle[] | null> {
  try {
    const url = `${WORKER_BASE}/api/v1/rebuses?language=${language}&age_group=${ageGroup}`
    const res = await fetch(url, { signal: AbortSignal.timeout(5000) })
    if (!res.ok) return null

    const data: RebusResponse = await res.json()
    if (!data.rebuses.length) return null

    // Shuffle and take `count` (wrap around if needed)
    const shuffled = [...data.rebuses].sort(() => Math.random() - 0.5)
    const result: StaticRebusPuzzle[] = []
    while (result.length < count) {
      result.push(...shuffled
        .slice(0, Math.min(count - result.length, shuffled.length))
        .map(r => ({ tiles: r.tiles, answer: r.target_word, hint: r.hint }))
      )
    }
    return result.slice(0, count)
  } catch {
    return null
  }
}

// ─── Word bank ────────────────────────────────────────────────────────────────

/**
 * Fetch approved words for a language + age-group from D1.
 * Returns null if the request fails (caller should fall back to static bank).
 */
export async function fetchWordBank(
  language:  Language,
  ageGroup:  AgeGroup,
): Promise<WordEntry[] | null> {
  try {
    const url = `${WORKER_BASE}/api/v1/words?language=${language}&age_group=${ageGroup}`
    const res = await fetch(url, { signal: AbortSignal.timeout(5000) })
    if (!res.ok) return null

    const data: WordsResponse = await res.json()

    // Map to the WordEntry shape the generator expects.
    // Each word may have multiple clues; we pick the first non-empty one.
    return data.words
      .map(w => ({
        word: w.word,
        clue: w.clues.find(c => Boolean(c)) ?? w.word,
      }))
      .filter(w => w.word.length >= 3)
  } catch {
    return null
  }
}
